/*
 * Three SuperAdmin registries that share one shape, kept in one file because each is small and
 * splitting them would be three copies of the same imports.
 *
 * - Figma `125:682` — SA3-07 · Leads
 * - Figma `125:351` — SA3-05 · Access Tokens
 * - Figma `125:507` — SA3-06 · Audit Log
 *
 * All three are presentational: SuperAdmin already loads and holds this data, and the revoke action
 * carries its own confirmation rules there. They draw what they are handed.
 *
 * The tokens screen is the one with teeth. An access token *is* the credential — `AccessTokenDto`
 * carries the decrypted value — so the registry shows the preview and never the token, and reveal
 * stays where it already is. What the screen adds is the thing the frame asks for: a token's state
 * said in words, because revoked, expired and used-up are three different reasons a token stopped
 * working and they need three different fixes.
 */
import { useState } from 'react';
import {
  Alert, Button, ConfirmDialog, KpiCard, Select, StatusBadge, TableTemplate,
} from '../..';
import type { BadgeTone, Column } from '../..';
import type { Destination } from '../../destinations';
import type { AccessTokenDto, AuditRowDto, OrganizerLeadDto, TenantSummaryDto } from '../../../api';

const when = (iso: string) => new Date(iso).toLocaleString('th-TH');

/* ── Leads ────────────────────────────────────────────────────────────────────────────────── */

export function LeadsScreen({ destination, leads, loading, error, onRetry }: {
  destination: Destination;
  leads: readonly OrganizerLeadDto[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}) {
  const columns: Column<OrganizerLeadDto>[] = [
    { key: 'name', header: 'ชื่อผู้ติดต่อ', cell: (l) => l.name || '—' },
    { key: 'phone', header: 'เบอร์โทร', width: '180px', cell: (l) => <span className="jt-mono">{l.phone}</span> },
    { key: 'created', header: 'ติดต่อเข้ามาเมื่อ', width: '220px', cell: (l) => when(l.createdAt) },
  ];

  const week = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const recent = leads.filter((l) => new Date(l.createdAt).getTime() >= week).length;

  return (
    <TableTemplate
      gate={{ destination, loading, error: error ?? null, onRetry }}
      kpis={[
        <KpiCard key="all" label="ผู้ติดต่อทั้งหมด" value={leads.length.toLocaleString('th-TH')} />,
        <KpiCard key="week" label="ใน 7 วันล่าสุด" value={recent.toLocaleString('th-TH')} tone={recent > 0 ? 'positive' : undefined} />,
      ]}
      table={{
        label: 'ผู้จัดที่ติดต่อเข้ามา',
        columns,
        rows: leads,
        rowKey: (l) => l.id,
        emptyTitle: 'ยังไม่มีใครติดต่อเข้ามา',
        emptyText: 'ฟอร์มติดต่อบนหน้าแรกจะส่งรายชื่อมาที่นี่',
        card: (l) => (
          <span className="jt-cell-stack">
            <span>{l.name || '—'}</span>
            <span className="jt-cell-sub jt-mono">{l.phone} · {when(l.createdAt)}</span>
          </span>
        ),
      }}
    />
  );
}

/* ── Access tokens ────────────────────────────────────────────────────────────────────────── */

type TokenState = { label: string; tone: BadgeTone; why: string };

/** Revoked, expired and used-up are three different reasons a token stopped working. */
export function tokenState(t: AccessTokenDto): TokenState {
  if (t.revoked) return { label: 'ถูกเพิกถอน', tone: 'danger', why: 'มีคนเพิกถอนไว้ — ออกใบใหม่ถ้ายังต้องใช้' };
  if (t.expired) return { label: 'หมดอายุ', tone: 'warning', why: 'เลยวันหมดอายุแล้ว — ต่ออายุไม่ได้ ต้องออกใหม่' };
  if (t.usedUp) return { label: 'ใช้ครบแล้ว', tone: 'neutral', why: 'เป็น token ใช้ครั้งเดียวและถูกใช้ไปแล้ว' };
  return { label: 'ใช้งานได้', tone: 'success', why: 'ยังเปิดใช้งานอยู่' };
}

export function AccessTokensScreen({
  destination, tokens, loading, error, onRetry, onRevoke, onCreate,
}: {
  destination: Destination;
  tokens: readonly AccessTokenDto[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onRevoke: (id: string) => void;
  onCreate?: () => void;
}) {
  const [confirm, setConfirm] = useState<AccessTokenDto | null>(null);
  const live = tokens.filter((t) => !t.revoked && !t.expired && !t.usedUp);

  const columns: Column<AccessTokenDto>[] = [
    {
      key: 'holder',
      header: 'ผู้ถือ',
      cell: (t) => (
        <span className="jt-cell-stack">
          <span>{t.holderLabel || '—'}</span>
          {/* The preview, never the token. The full value lives behind the existing reveal. */}
          <span className="jt-cell-sub jt-mono">{t.tokenPreview}</span>
        </span>
      ),
    },
    { key: 'event', header: 'อีเวนต์', cell: (t) => t.eventName || t.eventSlug },
    { key: 'role', header: 'สิทธิ์', width: '120px', cell: (t) => <StatusBadge tone="neutral">{t.role}</StatusBadge> },
    { key: 'kind', header: 'ชนิด', width: '130px', cell: (t) => (t.kind === 'single' ? 'ใช้ครั้งเดียว' : 'ใช้ซ้ำได้') },
    { key: 'expires', header: 'หมดอายุ', width: '190px', cell: (t) => (t.expiresAt ? when(t.expiresAt) : 'ไม่มีกำหนด') },
    {
      key: 'state',
      header: 'สถานะ',
      width: '200px',
      cell: (t) => {
        const s = tokenState(t);
        return (
          <span className="jt-cell-stack">
            <StatusBadge tone={s.tone}>{s.label}</StatusBadge>
            <span className="jt-cell-sub">{s.why}</span>
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      width: '110px',
      align: 'end',
      cell: (t) => (tokenState(t).label === 'ใช้งานได้' ? (
        <Button size="sm" variant="ghost" onClick={() => setConfirm(t)}>เพิกถอน</Button>
      ) : null),
    },
  ];

  return (
    <TableTemplate
      gate={{ destination, loading, error: error ?? null, onRetry }}
      kpis={[
        <KpiCard key="live" label="Token ที่ใช้งานได้" value={live.length.toLocaleString('th-TH')} />,
        <KpiCard key="all" label="ออกไปแล้วทั้งหมด" value={tokens.length.toLocaleString('th-TH')} />,
        <KpiCard
          key="revoked"
          label="ถูกเพิกถอน"
          value={tokens.filter((t) => t.revoked).length.toLocaleString('th-TH')}
        />,
      ]}
      toolbar={onCreate && <Button size="sm" variant="primary" onClick={onCreate}>＋ ออก Token ใหม่</Button>}
      table={{
        label: 'Access token',
        columns,
        rows: tokens,
        rowKey: (t) => t.id,
        emptyTitle: 'ยังไม่มี Token',
        emptyText: 'ออก token ให้ทีมงานหรือจุดเช็กอินเพื่อให้เข้าถึงอีเวนต์ได้',
        card: (t) => (
          <span className="jt-cell-stack">
            <span>{t.holderLabel || '—'} · {t.role}</span>
            <span className="jt-cell-sub jt-mono">{t.tokenPreview} · {t.eventSlug}</span>
            <StatusBadge tone={tokenState(t).tone}>{tokenState(t).label}</StatusBadge>
          </span>
        ),
      }}
      drawer={
        <ConfirmDialog
          open={!!confirm}
          onClose={() => setConfirm(null)}
          onConfirm={() => {
            const target = confirm;
            setConfirm(null);
            if (target) onRevoke(target.id);
          }}
          title={`เพิกถอน token ของ ${confirm?.holderLabel || '—'}`}
          confirmLabel="เพิกถอนเดี๋ยวนี้"
          consequences={[
            `อุปกรณ์ที่ใช้ token นี้อยู่จะเข้าใช้งาน ${confirm?.eventName || confirm?.eventSlug || 'อีเวนต์'} ไม่ได้ทันที`,
            'ถ้าเป็นเครื่องสแกนที่หน้างาน คนที่ถืออยู่จะสแกนไม่ได้จนกว่าจะได้ token ใหม่',
            'ย้อนกลับไม่ได้ — ถ้ายังต้องใช้ต้องออก token ใหม่และส่งให้ผู้ถืออีกครั้ง',
          ]}
        />
      }
    />
  );
}

/* ── Audit log ────────────────────────────────────────────────────────────────────────────── */

export function AuditLogScreen({
  destination, rows, events, actions, eventFilter, actionFilter,
  onEventFilterChange, onActionFilterChange, loading, error, onRetry,
}: {
  destination: Destination;
  rows: readonly AuditRowDto[];
  events: readonly TenantSummaryDto[];
  actions: readonly string[];
  eventFilter: string;
  actionFilter: string;
  onEventFilterChange: (v: string) => void;
  onActionFilterChange: (v: string) => void;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}) {
  const columns: Column<AuditRowDto>[] = [
    { key: 'when', header: 'เมื่อ', width: '200px', cell: (r) => when(r.createdAt) },
    { key: 'actor', header: 'ใคร', width: '170px', cell: (r) => r.actor },
    { key: 'action', header: 'ทำอะไร', width: '240px', cell: (r) => <span className="jt-mono">{r.action}</span> },
    { key: 'event', header: 'อีเวนต์', width: '190px', cell: (r) => r.eventName ?? '— ทั้งแพลตฟอร์ม' },
    { key: 'detail', header: 'รายละเอียด', cell: (r) => r.detail },
  ];

  const filtered = eventFilter !== '' || actionFilter !== '';

  return (
    <TableTemplate
      gate={{ destination, loading, error: error ?? null, onRetry }}
      toolbar={
        <>
          <Select
            label="อีเวนต์"
            value={eventFilter}
            placeholder="ทุกอีเวนต์"
            options={[{ value: '', label: 'ทุกอีเวนต์' },
              ...events.map((e) => ({ value: e.id, label: e.name || e.slug }))]}
            onChange={(e) => onEventFilterChange(e.target.value)}
          />
          <Select
            label="การกระทำ"
            value={actionFilter}
            options={[{ value: '', label: 'ทุกการกระทำ' },
              ...actions.map((a) => ({ value: a, label: a }))]}
            onChange={(e) => onActionFilterChange(e.target.value)}
          />
        </>
      }
      summary={
        <Alert tone="info" title="บันทึกนี้อ่านอย่างเดียว">
          แถวใน audit log แก้หรือลบไม่ได้จากหน้าจอใด — เป็นหลักฐานว่าใครทำอะไรกับอีเวนต์ไหน
        </Alert>
      }
      table={{
        label: 'Audit log',
        columns,
        rows,
        rowKey: (r) => r.id,
        filtered,
        onClearFilters: () => { onEventFilterChange(''); onActionFilterChange(''); },
        emptyTitle: 'ยังไม่มีบันทึก',
        emptyText: 'การกระทำที่ถูกบันทึกจะปรากฏที่นี่',
        card: (r) => (
          <span className="jt-cell-stack">
            <span className="jt-mono">{r.action}</span>
            <span className="jt-cell-sub">{r.actor} · {when(r.createdAt)}</span>
            <span className="jt-cell-sub">{r.detail}</span>
          </span>
        ),
      }}
    />
  );
}
